import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import { allModels } from './ProductPage.tsx';
import { useLanguage } from '../../i18n/LanguageContext';

const BASE_URL = 'https://www.hestiadoors.com';

// contentKey: 'categories.exteriorDoors' | 'categories.storageDoors'
// seoKey: 'seo.exteriorDoors' | 'seo.storageDoors'
const DoorCategory = ({ contentKey, seoKey, canonicalPath, modelSlugs }: {
  contentKey: string;
  seoKey: string;
  canonicalPath: string;
  modelSlugs: string[];
}) => {
  const { t, pick, path, lang } = useLanguage();

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '3rem 1.5rem'
  };

  const highlightStyle = { color: '#dfa926' };

  const models = modelSlugs
    .map((slug) => allModels.find((e: any) => e.model.slug === slug))
    .filter(Boolean);

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('productPage.breadcrumbHome'), item: `${BASE_URL}${lang === 'et' ? '/et' : '/'}` },
      { '@type': 'ListItem', position: 2, name: t(`${contentKey}.h1`) }
    ]
  };

  const benefits = t(`${contentKey}.benefits`) as unknown as string[];

  return (
    <div style={pageStyle}>
      <SEO
        title={t(`${seoKey}.title`)}
        description={t(`${seoKey}.description`)}
        canonicalPath={canonicalPath}
        keywords={t(`${seoKey}.keywords`)}
        jsonLd={breadcrumbLd}
      />
      <Navigation />

      <div style={containerStyle}>
        <nav aria-label="Breadcrumb" style={{ marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          <ol style={{ listStyle: 'none', display: 'flex', gap: '0.5rem', padding: 0, margin: 0 }}>
            <li><Link to={path('/')} style={{ color: '#9ca3af', textDecoration: 'none' }}>{t('productPage.breadcrumbHome')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li><Link to={path('/products')} style={{ color: '#9ca3af', textDecoration: 'none' }}>{t('productPage.breadcrumbProducts')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li style={{ color: '#dfa926' }} aria-current="page">{t(`${contentKey}.h1`)}</li>
          </ol>
        </nav>

        <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white', marginBottom: '1.25rem', maxWidth: '56rem' }}>
          {t(`${contentKey}.h1`)}
        </h1>
        <p style={{ fontSize: '1.25rem', color: '#d1d5db', maxWidth: '48rem', lineHeight: 1.7, marginBottom: '2.5rem' }}>
          {t(`${contentKey}.intro`)}
        </p>

        {/* Benefits */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ ...highlightStyle, fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.25rem' }}>
            {t(`${contentKey}.benefitsTitle`)}
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.75rem 2rem' }}>
            {benefits.map((b: string, i: number) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', color: '#d1d5db' }}>
                <span style={{ width: '0.5rem', height: '0.5rem', backgroundColor: '#dfa926', borderRadius: '50%', marginTop: '0.4rem', marginRight: '0.75rem', flexShrink: 0 }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Products */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ ...highlightStyle, fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.25rem' }}>
            {t(`${contentKey}.productsTitle`)}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1.25rem' }}>
            {models.map((e: any) => (
              <Link
                key={e.model.slug}
                to={path(`/products/${e.model.slug}`)}
                style={{
                  background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
                  border: '1px solid rgba(58, 58, 58, 0.5)',
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  transition: 'border-color 0.2s, transform 0.2s'
                }}
                onMouseEnter={(ev) => {
                  ev.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                  ev.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(ev) => {
                  ev.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                  ev.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <img
                  src={e.model.image}
                  alt={`${pick(e.model, 'name')} — ${pick(e.group, 'name')}`}
                  loading="lazy"
                  style={{ width: '100%', height: '220px', objectFit: 'cover' as const, display: 'block', background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)' }}
                />
                <div style={{ padding: '1rem' }}>
                  <span style={{ color: '#dfa926', fontWeight: 600 }}>{pick(e.model, 'name')}</span>
                  <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: '0.35rem 0 0 0', lineHeight: 1.5 }}>
                    {pick(e.model, 'description')}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0 0' }}>
                    {(pick(e.model, 'keyFeatures') as string[]).slice(0, 2).map((f: string, i: number) => (
                      <li key={i} style={{ color: '#d1d5db', fontSize: '0.8rem', display: 'flex', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
                        <span style={{ width: '0.3rem', height: '0.3rem', backgroundColor: '#dfa926', borderRadius: '50%', marginTop: '0.35rem', marginRight: '0.5rem', flexShrink: 0 }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div style={{ textAlign: 'center' as const, padding: '2rem 0' }}>
          <Link
            to={path('/contact')}
            style={{ display: 'inline-block', backgroundColor: '#dfa926', color: 'black', fontWeight: 600, padding: '1rem 2.5rem', borderRadius: '0.5rem', textDecoration: 'none' }}
          >
            {t(`${contentKey}.cta`)}
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DoorCategory;
